import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, ArrowUpRight, MessageCircle, X, ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { waLink, quoteMessage } from "@/lib/whatsapp";
import TankPlaceholder from "@/components/TankPlaceholder";

function PageHeader({ eyebrow, title, subtitle, image }) {
  return (
    <section className="relative min-h-[52vh] bg-pipeco-dark text-white flex items-end overflow-hidden">
      {image && (<><img src={image} alt="" className="absolute inset-0 w-full h-full object-cover opacity-45"/><div className="absolute inset-0 bg-gradient-to-t from-pipeco-dark via-black/60 to-transparent"/></>)}
      <div className="relative container-x px-6 md:px-10 py-24 md:py-32">
        <div className="overline text-emerald-300">{eyebrow}</div>
        <h1 className="headline text-5xl md:text-7xl mt-4 max-w-4xl">{title}</h1>
        {subtitle && <p className="text-white/70 mt-5 max-w-2xl">{subtitle}</p>}
      </div>
    </section>
  );
}

export function ProjectsList() {
  const [projects, setProjects] = useState([]);
  const [filters, setFilters] = useState({ country: "All", tank_type: "All", industry: "All", status: "All" });
  useEffect(() => { api.get("/projects").then(r => setProjects(r.data)); }, []);

  const opts = useMemo(() => {
    const uniq = (key) => ["All", ...Array.from(new Set(projects.map(p => p[key]).filter(Boolean)))];
    return { country: uniq("country"), tank_type: uniq("tank_type"), industry: uniq("industry"), status: uniq("status") };
  }, [projects]);

  const filtered = projects.filter(p =>
    (filters.country === "All" || p.country === filters.country) &&
    (filters.tank_type === "All" || p.tank_type === filters.tank_type) &&
    (filters.industry === "All" || p.industry === filters.industry) &&
    (filters.status === "All" || p.status === filters.status)
  );

  return (
    <div data-testid="projects-page">
      <PageHeader eyebrow="Featured Projects" title="Water Storage Delivered Worldwide" image="/tanks/projects-hero-v2.webp"/>
      <section className="section-pad bg-white">
        <div className="container-x px-6 md:px-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10">
            {["country","tank_type","industry","status"].map(key => (
              <select key={key} data-testid={`filter-${key}`} value={filters[key]} onChange={(e)=>setFilters({...filters, [key]: e.target.value})}
                className="w-full border border-black/10 px-4 py-3 text-sm bg-white focus:outline-none focus:border-pipeco-green">
                {opts[key].map(o => <option key={o} value={o}>{key === "tank_type" ? "Tank: " : key === "country" ? "Country: " : key === "industry" ? "Industry: " : "Status: "}{o}</option>)}
              </select>
            ))}
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map(pr => (
              <Link key={pr.id} to={`/projects/${pr.id}`} data-testid={`project-card-${pr.id}`} className="group block card-lift">
                <div className="aspect-4-5 overflow-hidden bg-pipeco-green">
                  {pr.main_image
                    ? <img src={pr.main_image} alt={pr.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"/>
                    : <TankPlaceholder label={pr.tank_type} subtitle={`${pr.country} · ${pr.capacity}`} tone="dark"/>}
                </div>
                <div className="pt-5">
                  <div className="overline text-pipeco-green">{pr.country} · {pr.tank_type}</div>
                  <h3 className="font-display text-xl mt-2 text-slate-900">{pr.name}</h3>
                  <div className="mt-2 text-xs text-slate-500 uppercase tracking-widest">{pr.capacity} · {pr.status}</div>
                </div>
              </Link>
            ))}
          </div>
          {filtered.length === 0 && <div className="text-center py-16 text-slate-500">No projects match your filters.</div>}
        </div>
      </section>
    </div>
  );
}

export function ProjectDetail() {
  const { id } = useParams();
  const [pr, setPr] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  useEffect(() => { api.get(`/projects/${id}`).then(r => setPr(r.data)); }, [id]);
  if (!pr) return <div className="section-pad container-x">Loading...</div>;
  const hasImg = !!pr.main_image;
  const gallery = pr.gallery && pr.gallery.length ? pr.gallery : (hasImg ? [pr.main_image] : []);
  return (
    <div data-testid={`project-detail-${id}`}>
      <PageHeader eyebrow={`${pr.country} · ${pr.tank_type}`} title={pr.name} subtitle={pr.description} image={hasImg ? pr.main_image : ""}/>
      <section className="section-pad bg-white">
        <div className="container-x px-6 md:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-8">
            {gallery.length > 0 ? (
              <div className="grid grid-cols-2 gap-3">
                {gallery.map((g,i) => (
                  <button key={i} onClick={()=>setLightbox(i)} data-testid={`gallery-thumb-${i}`} className={`overflow-hidden bg-slate-100 ${i===0?"col-span-2 aspect-16-9":"aspect-4-5"}`}>
                    <img src={g} alt={`${pr.name} ${i}`} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"/>
                  </button>
                ))}
              </div>
            ) : (
              <div className="aspect-16-9"><TankPlaceholder label={pr.name} subtitle={`${pr.country} · ${pr.tank_type}`} tone="dark"/></div>
            )}
            <div className="mt-10">
              <div className="overline text-pipeco-green mb-3">Project Description</div>
              <p className="text-slate-700 leading-relaxed">{pr.description}</p>
              <div className="grid sm:grid-cols-2 gap-6 mt-8">
                {[["Scope of Supply", pr.scope],["Products Supplied", pr.products_supplied],["Installation Details", pr.installation_details]].map(([k,v]) => v?(
                  <div key={k}><div className="overline text-pipeco-green mb-2">{k}</div><p className="text-sm text-slate-700">{v}</p></div>
                ):null)}
              </div>
            </div>
          </div>
          <aside className="lg:col-span-4">
            <div className="bg-pipeco-neutral p-8 border border-black/5">
              <div className="overline text-pipeco-green">Project Data</div>
              <dl className="mt-4 space-y-3 text-sm">
                {[["Country",pr.country],["City",pr.city],["Industry",pr.industry],["Tank Type",pr.tank_type],["Capacity",pr.capacity],["Number of Tanks",pr.number_of_tanks],["Status",pr.status],["Completion Date",pr.completion_date]].map(([k,v])=>(
                  <div key={k} className="flex justify-between border-b border-black/5 pb-2"><dt className="text-slate-500 uppercase tracking-widest text-[10px]">{k}</dt><dd className="text-slate-900 font-medium text-right">{v || "—"}</dd></div>
                ))}
              </dl>
              <a href={waLink("971504245888", `Hello PIPECO GROUP,\n\nI’m interested in a similar *${pr.tank_type} Tank* for my project. Could you please share more details and advise on the most suitable option?\n\n*Page reference:*\n${window.location.href}\n\nThank you.`)} target="_blank" rel="noreferrer" data-testid="project-quote-btn" className="btn-whatsapp w-full justify-center mt-6"><MessageCircle size={14}/>Similar Project? Chat</a>
            </div>
          </aside>
        </div>
      </section>
      {lightbox !== null && gallery.length > 0 && (
        <div className="fixed inset-0 z-[70] bg-black/95 grid place-items-center p-4" onClick={()=>setLightbox(null)}>
          <button data-testid="lightbox-close" className="absolute top-4 right-4 text-white p-2" onClick={()=>setLightbox(null)}><X size={26}/></button>
          <button data-testid="lightbox-prev" className="absolute left-4 text-white p-2" onClick={(e)=>{e.stopPropagation(); setLightbox((lightbox-1+gallery.length)%gallery.length);}}><ChevronLeft size={30}/></button>
          <img src={gallery[lightbox]} alt="" className="max-h-[92vh] max-w-[92vw] object-contain" onClick={(e)=>e.stopPropagation()}/>
          <button data-testid="lightbox-next" className="absolute right-4 text-white p-2" onClick={(e)=>{e.stopPropagation(); setLightbox((lightbox+1)%gallery.length);}}><ChevronRight size={30}/></button>
        </div>
      )}
    </div>
  );
}

export default ProjectsList;
